import copy from '../copy';
import * as validators from '../helpers/validators';

const { errorMessages } = copy;

const isEmptyString = validators.checkString({ maxLength: 0 });
const isNonEmptyString = validators.checkString({ minLength: 1 });

export const userTypes = {
    CONSUMER: 'consumer',
    DEALER: 'dealer',
    TRADE: 'trade',
} as const;

export const questionNames = {
    INTENT: 'INTENT',
    CATEGORIES: 'CATEGORIES',
} as const;

export const answerNames = {
    GIFT: 'GIFT',
} as const;

export const tradeApplicationInfoKey = 'tradeApplicationInfo' as const;
export const steps = {
    USER_TYPE_SELECTION: 'user_type_selection',
    CONSUMER_BENEFITS: 'consumer_benefits',
    CONSUMER_COMPLETE: 'consumer_complete',
    CONSUMER_INFO: 'consumer_info',
    DEALER_INFO: 'dealer_info',
    TRADE_BASIC_INFO: 'trade_basic_info',
    TRADE_ALT_VERIFICATION: 'trade_alt_verification',
    TRADE_FIRM_PROFILE: 'trade_firm_profile',
    TRADE_REFERRAL: 'trade_referral',
    TRADE_COMPLETE: 'trade_complete',
    PROMO_CODE: 'promo_code',
    USER_SURVEY: 'user_survey',
    USER_SURVEY_CONFIRMATION: 'user_survey_confirmation',
    USER_SURVEY_GIFT_CONFIRMATION: 'user_survey_gift_confirmation',
} as const;

export const gaLabels = {
    PAGEVIEW_LIMIT_REACHED: 'pageview limit reached',
} as const;

export const events = {
    USER_CHANGED: 'user_changed',
} as const;

const registrationLeadSourceLabel = 'Web: User Registration' as const;
export const flowActions = {
    AUTO_PROMPT_REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    LOGIN: {
        leadSourceLabel: 'Web: User Login',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    SOCIAL_REGISTER_FACEBOOK: {
        leadSourceLabel: 'Web: Social Registration',
        tradeLeadSourceLabel: 'Web: Social Registration',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    SOCIAL_REGISTER_GOOGLE: {
        leadSourceLabel: 'Web: Social Registration',
        tradeLeadSourceLabel: 'Web: Social Registration',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_BASIC_INFO,
    },
    SUSPECTED_TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_BASIC_INFO,
    },
    TRADE_REAPPLY: {
        leadSourceLabel: 'Firmless: Reapplied',
        startingStep: steps.TRADE_BASIC_INFO,
    },
    TRADE_FIRM_INFO: {
        leadSourceLabel: 'Web: Trade Firm Info',
        startingStep: steps.TRADE_FIRM_PROFILE,
    },
    ALT_REGISTRATION_CONSUMER_FLOW: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_SURVEY,
    },
} as const;

export const footers = {
    ASK_LATER: 'ask_later_footer',
    REQUEST_PASSWORD_EMAIL: 'request_password_email_footer',
    LOGIN: 'login_footer',
    REGISTER: 'register_footer',
    SKIP_QUESTION: 'skip_question',
} as const;

export const tradeFormFields = {
    FIRST_NAME: 'FIRST_NAME',
    LAST_NAME: 'LAST_NAME',
    FIRM_NAME: 'FIRM_NAME',
    FIRM_WEBSITE: 'FIRM_WEBSITE',
    PHONE_NUMBER: 'PHONE_NUMBER',
    EMAIL: 'EMAIL',
    HAS_OFFICIAL_DOCS: 'HAS_OFFICIAL_DOCS',
    TRADE_DOCUMENT: 'TRADE_DOCUMENT',
    SOCIAL_NETWORK: 'SOCIAL_NETWORK',
    SOCIAL_USERNAME: 'SOCIAL_USERNAME',
    FIRM_SIZE: 'FIRM_SIZE',
    ROLE_AT_FIRM: 'ROLE_AT_FIRM',
    FIRM_TYPE: 'FIRM_TYPE',
    FIRM_ADDRESS: 'FIRM_ADDRESS',
    FIRM_POSTAL_CODE: 'FIRM_POSTAL_CODE',
    FIRM_COUNTRY: 'FIRM_COUNTRY',
    EXISTING_USER: 'EXISTING_USER',
    FIRM_PROJECTS_PER_YEAR: 'FIRM_PROJECTS_PER_YEAR',
    REASON_FOR_JOINING: 'REASON_FOR_JOINING',
    TRADE_USER_STATUS: 'TRADE_USER_STATUS',
} as const;

export const tradeFormFieldsConfig = {
    [tradeFormFields.FIRST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firstName,
        validator: isNonEmptyString,
    },
    [tradeFormFields.LAST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.lastName,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmName,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_WEBSITE]: {
        defaultValue: '',
        validator: validators.oneOf(isEmptyString, validators.isValidUrl),
    },
    [tradeFormFields.PHONE_NUMBER]: {
        defaultValue: '',
        errorMessage: errorMessages?.phoneNumber,
        validator: validators.checkString({ minLength: 9 }),
    },
    [tradeFormFields.EMAIL]: {
        defaultValue: '',
        errorMessage: errorMessages?.emailInvalid,
        validator: validators.isValidEmail,
    },
    [tradeFormFields.HAS_OFFICIAL_DOCS]: {
        defaultValue: false,
    },
    [tradeFormFields.TRADE_DOCUMENT]: {
        defaultValue: null,
    },
    [tradeFormFields.SOCIAL_NETWORK]: {
        defaultValue: '',
    },
    [tradeFormFields.SOCIAL_USERNAME]: {
        defaultValue: '',
    },
    [tradeFormFields.FIRM_SIZE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmSize,
        validator: isNonEmptyString,
    },
    [tradeFormFields.ROLE_AT_FIRM]: {
        defaultValue: '',
        errorMessage: errorMessages?.roleAtFirm,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_TYPE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmType,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_ADDRESS]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmAddress,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_POSTAL_CODE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmZipCode,
        validator: validators.all(isNonEmptyString, validators.isValidZipcode),
    },
    [tradeFormFields.FIRM_COUNTRY]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmCountry,
        validator: isNonEmptyString,
    },
    [tradeFormFields.EXISTING_USER]: {
        defaultValue: false,
    },
    [tradeFormFields.FIRM_PROJECTS_PER_YEAR]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmProjectsPerYear,
        validator: isNonEmptyString,
    },
    [tradeFormFields.REASON_FOR_JOINING]: {
        defaultValue: '',
        errorMessage: errorMessages?.reasonForJoining,
        validator: isNonEmptyString,
    },
    [tradeFormFields.TRADE_USER_STATUS]: {
        defaultValue: '',
    },
} as const;
